import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeetingRepository } from 'repository/meetingRepository';
import { useBackDismiss } from 'hooks/useBackDismiss';
import { useAlert } from 'contexts/AlertContext';
import { IcClose, IcCalendar, IcEdit, IcUsers } from 'components/common/icons/EmojiIcons';
import styles from './ParticipantSourceSheet.module.css';

/** 클래스 이름을 해시된 이름으로 바꿉니다. 모르는 이름은 그대로 둡니다.
 *  이름을 바꾸지 않고 스코프만 씌우려고 둔 얇은 헬퍼입니다. */
const cls = (names?: string): string =>
    (names || '').split(' ').filter(Boolean)
        .map((n) => (styles as Record<string, string>)[n] || n).join(' ');

export interface ParticipantItem {
    userNo: number;
    nickname: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    onPick: (participants: ParticipantItem[]) => void;
}

type Tab = 'meeting' | 'manual';

const safeDecode = (s?: string) => {
    if (!s) return s || '';
    try { return decodeURIComponent(s); } catch { return s; }
};

const ParticipantSourceSheet: React.FC<Props> = ({ open, onClose, onPick }) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [tab, setTab] = useState<Tab>('meeting');
    const [names, setNames] = useState<string[]>([]);
    const [input, setInput] = useState('');
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const [meetings, setMeetings] = useState<any[]>([]);
    const [listStatus, setListStatus] = useState<'idle' | 'pending' | 'ready' | 'error'>('idle');
    // 모임별 멤버 캐시 (meetingId → members[])
    const [memberCache, setMemberCache] = useState<Record<string, ParticipantItem[]>>({});
    /* 자주 만난 사람 — 서버가 함께한 횟수 내림차순으로 주므로 그대로 씁니다.
       모임 초대·번개가 쓰는 것과 같은 API 입니다. */
    const [frequent, setFrequent] = useState<Array<{ userNo: number; nickname: string; sharedCount: number }>>([]);

    // 시트 마운트 시에만 독립적으로 fetch — 공용 meetingStore 는 건드리지 않음
    // (공용 store 를 리셋/재요청하면 홈이 pending 으로 전환돼 시트가 unmount 됨)
    useEffect(() => {
        if (!open) return;
        setTab('meeting');
        setNames([]);
        setInput('');
        setListStatus('pending');
        /* 자주 만난 사람 — 사람을 한 명씩 고를 때 매번 이름을 치지 않게 합니다.
           안 불려와도 이름 입력은 그대로 되므로 실패는 조용히 넘깁니다. */
        MeetingRepository.onGetFrequentCoMembersByUser(20)
            .then((res: any) => {
                const raw = res?.data?.data ?? res?.data ?? [];
                const list = (Array.isArray(raw) ? raw : []).map((r: any) => ({
                    userNo: Number(r.userNo),
                    nickname: safeDecode(r.nickname || '') || t('picker.memberFallback'),
                    sharedCount: Number(r.sharedCount) || 0,
                })).filter((r: any) => r.userNo > 0);
                setFrequent(list);
            })
            .catch(() => setFrequent([]));
        MeetingRepository.onGetMyMeetings({ pageSize: 50 } as any)
            .then((res: any) => {
                // 서버 응답 구조: res.data = { data: [...], message, ... }
                const body = res?.data || {};
                const inner = body?.data;
                const merged: any[] = [];
                if (Array.isArray(inner)) merged.push(...inner);
                else if (inner && typeof inner === 'object') {
                    if (Array.isArray(inner.today)) merged.push(...inner.today);
                    if (Array.isArray(inner.ongoing)) merged.push(...inner.ongoing);
                    if (Array.isArray(inner.upcoming)) merged.push(...inner.upcoming);
                    if (Array.isArray(inner.list)) merged.push(...inner.list);
                }
                // 아직 종료되지 않은 모임만 (status !== 'ENDED' / 'CANCELED')
                const active = merged.filter(m => {
                    const s = (m.status || '').toUpperCase();
                    return s !== 'ENDED' && s !== 'CANCELED' && s !== 'CANCELLED';
                });
                // 중복 제거
                const seen = new Set();
                const unique = active.filter(m => {
                    const id = m.meetingId || m.id || m.meetingNo;
                    if (!id || seen.has(id)) return false;
                    seen.add(id);
                    return true;
                });
                setMeetings(unique);
                setListStatus('ready');
                // 멤버 프리뷰용으로 각 모임의 멤버 병렬 로드 (최대 20개까지)
                unique.slice(0, 20).forEach(async (mm) => {
                    const mid = mm.meetingId || mm.id || mm.meetingNo;
                    if (!mid) return;
                    try {
                        const r: any = await MeetingRepository.onGetMeetingMembers(mid);
                        const arr = Array.isArray(r?.data?.data) ? r.data.data : (Array.isArray(r?.data) ? r.data : []);
                        const members: ParticipantItem[] = arr.map((m: any, i: number) => ({
                            userNo: Number(m.userNo ?? m.memberNo ?? -(i + 1)),
                            nickname: safeDecode(m.nickname || m.name || t('picker.memberFallback')),
                        })).filter((p: ParticipantItem) => p.nickname);
                        setMemberCache(prev => ({ ...prev, [mid]: members }));
                    } catch {}
                });
            })
            .catch(() => {
                setMeetings([]);
                setListStatus('error');
            });
    }, [open]);

    useBackDismiss(open, onClose);

    if (!open) return null;

    const status = listStatus;

    // 개별 모임의 멤버를 로드 (캐시 사용). 멤버 이름 프리뷰용.
    const loadMembers = async (id: string): Promise<ParticipantItem[]> => {
        if (memberCache[id]) return memberCache[id];
        const res: any = await MeetingRepository.onGetMeetingMembers(id);
        const arr = Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res?.data) ? res.data : []);
        const members: ParticipantItem[] = arr.map((mm: any, i: number) => ({
            userNo: Number(mm.userNo ?? mm.memberNo ?? -(i + 1)),
            nickname: safeDecode(mm.nickname || mm.name || t('picker.memberFallback')),
        })).filter((p: ParticipantItem) => p.nickname);
        setMemberCache(prev => ({ ...prev, [id]: members }));
        return members;
    };

    const pickMeeting = async (m: any) => {
        if (loadingId) return;
        const id = m.meetingId || m.id || m.meetingNo;
        if (!id) return;
        setLoadingId(id);
        try {
            const members = await loadMembers(id);
            if (members.length < 2) {
                showAlert({ title: t('picker.needMoreTitle'), message: t('picker.needMoreMsg') });
                return;
            }
            onPick(members);
        } catch {
            showAlert({ title: t('picker.error'), message: t('picker.loadMemberFail') });
        } finally {
            setLoadingId(null);
        }
    };

    /** 이름 하나 더하기. 중복과 10명 상한은 여기서만 판정합니다. */
    const pushName = (raw: string) => {
        const n = raw.trim();
        if (!n) return;
        setNames(prev => (prev.length >= 10 || prev.includes(n) ? prev : [...prev, n]));
    };

    const addName = () => {
        pushName(input);
        setInput('');
    };
    const removeName = (i: number) => setNames(names.filter((_, idx) => idx !== i));

    const confirmManual = () => {
        if (names.length < 2) return;
        const participants: ParticipantItem[] = names.map((n, i) => ({
            userNo: -(i + 1),
            nickname: n,
        }));
        onPick(participants);
    };

    return (
        <div className={cls("psrc-overlay")} onClick={onClose}>
            <div className={cls("psrc-sheet")} onClick={e => e.stopPropagation()}>
                <header className={cls("psrc-header")}>
                    <span>{t('picker.title')}</span>
                    <button className={cls("psrc-close")} onClick={onClose}><IcClose size={16} /></button>
                </header>
                <div className={cls("psrc-tabs")}>
                    <button
                        className={cls(`psrc-tab ${tab === 'meeting' ? 'active' : ''}`)}
                        onClick={() => setTab('meeting')}
                    ><IcCalendar size={14} /> {t('picker.fromMeeting')}</button>
                    <button
                        className={cls(`psrc-tab ${tab === 'manual' ? 'active' : ''}`)}
                        onClick={() => setTab('manual')}
                    ><IcEdit size={14} /> {t('picker.manualTab')}</button>
                </div>

                {tab === 'meeting' && (
                    <div className={cls("psrc-body")}>
                        {status === 'pending' && <div className={cls("psrc-empty")}>{t('picker.loadingMeetings')}</div>}
                        {status === 'ready' && meetings.length === 0 && (
                            <div className={cls("psrc-empty")}>{t('picker.noMeetings')}</div>
                        )}
                        <ul className={cls("psrc-meetings")}>
                            {meetings.map((m, i) => {
                                const id = m.meetingId || m.id || m.meetingNo;
                                const name = safeDecode(m.meetingName || m.name || t('picker.meetingFallback'));
                                const cnt = m.currentMemberCount || m.memberCount || 0;
                                const cached = id ? memberCache[id] : undefined;
                                const memberPreview = cached
                                    ? cached.map(p => p.nickname).join(', ')
                                    : t('picker.loadingMembers');
                                return (
                                    <li
                                        key={`${id}-${i}`}
                                        className={cls(`psrc-meeting ${loadingId === id ? 'loading' : ''}`)}
                                        onClick={() => pickMeeting(m)}
                                    >
                                        <div className={cls("psrc-meeting-info")}>
                                            <span className={cls("psrc-meeting-name")}>{name}</span>
                                            <span className={cls("psrc-meeting-members")} title={memberPreview}>
                                                <IcUsers size={13} /> {memberPreview}
                                            </span>
                                        </div>
                                        <span className={cls("psrc-meeting-count")}>{t('picker.countPeople', { count: cnt })}</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {tab === 'manual' && (
                    <div className={cls("psrc-body")}>
                        {/* 자주 만난 사람 — 많이 함께한 순서로 서버가 내려줍니다.
                            매번 이름을 치는 대신 눌러서 더합니다. */}
                        {frequent.length > 0 && (
                            <div className={cls("psrc-freq")}>
                                <div className={cls("psrc-freq-title")}>{t('picker.frequentTitle')}</div>
                                <ul className={cls("psrc-freq-list")}>
                                    {frequent.map(f => {
                                        const on = names.includes(f.nickname);
                                        const full = names.length >= 10 && !on;
                                        return (
                                            <li key={f.userNo}>
                                                <button
                                                    type="button"
                                                    className={cls(`psrc-freq-item ${on ? 'on' : ''}`)}
                                                    disabled={full}
                                                    onClick={() => (on
                                                        ? setNames(prev => prev.filter(n => n !== f.nickname))
                                                        : pushName(f.nickname))}
                                                >
                                                    <span className={cls("psrc-freq-name")}>{f.nickname}</span>
                                                    <span className={cls("psrc-freq-count")}>
                                                        {t('meetingInvite.sharedCount', { count: f.sharedCount })}
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        <div className={cls("psrc-input-row")}>
                            <input
                                className={cls("psrc-input")}
                                placeholder={t('picker.namePlaceholder')}
                                value={input}
                                onChange={e => setInput(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') addName(); }}
                                maxLength={10}
                            />
                            <button
                                className={cls("psrc-add")}
                                onClick={addName}
                                disabled={!input.trim() || names.length >= 10}
                            >+</button>
                        </div>
                        <ul className={cls("psrc-names")}>
                            {names.map((n, i) => (
                                <li key={i} className={cls("psrc-name-chip")}>
                                    <span>{n}</span>
                                    <button onClick={() => removeName(i)}><IcClose size={13} /></button>
                                </li>
                            ))}
                        </ul>
                        {names.length < 2 && (
                            <div className={cls("psrc-hint")}>{t('picker.needTwo', { count: names.length })}</div>
                        )}
                        <button
                            className={cls("psrc-confirm")}
                            onClick={confirmManual}
                            disabled={names.length < 2}
                        >{t('picker.start')}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ParticipantSourceSheet;
